import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import AlertToast, { type AlertAction, type AlertTone } from '@/components/base/AlertToast';

export interface ToastInput {
  title: string;
  description?: string;
  tone?: AlertTone;
  icon?: string;
  action?: AlertAction | null;
  duration?: number;
  /** When two alerts share a key, the newest replaces awareness of the older one. */
  dedupeKey?: string;
}

interface ActiveToast extends ToastInput {
  id: string;
}

interface ToastContextValue {
  pushToast: (input: ToastInput) => void;
  dismissToast: (id: string) => void;
}

/** Keep the on-screen stack short and calm — never a wall of alerts. */
const MAX_VISIBLE = 3;

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * Single toast centre for the dashboard. Any feature (notification arrivals,
 * live operational alerts) pushes here and the stack renders in one consistent,
 * brand-aware place. Alerts auto-dismiss; the user can also close them.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  const seq = useRef(0);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const pushToast = useCallback((input: ToastInput) => {
    seq.current += 1;
    const id = `TST-${seq.current}`;
    setToasts((prev) => {
      if (input.dedupeKey && prev.some((toast) => toast.dedupeKey === input.dedupeKey)) {
        return prev;
      }
      return [{ id, ...input }, ...prev].slice(0, MAX_VISIBLE);
    });
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({ pushToast, dismissToast }),
    [pushToast, dismissToast],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed bottom-4 end-4 z-[70] flex w-[calc(100vw-2rem)] max-w-[380px] flex-col gap-3"
        aria-live="polite"
        aria-atomic="false"
      >
        {toasts.map((toast) => (
          <AlertToast
            key={toast.id}
            title={toast.title}
            description={toast.description}
            tone={toast.tone}
            icon={toast.icon}
            action={toast.action}
            duration={toast.duration}
            onClose={() => dismissToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}